import { useCallback, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { APPLE_PRESS, APPLE_RELEASE, DELAY, prefersReducedMotion } from '@/tokens/motion';

interface AppleMotionResult {
  /** Ref to attach to the element */
  ref: RefObject<HTMLElement | null>;
  /** Style object to spread on the element */
  style: CSSProperties;
  /** Handlers to spread on the element */
  handlers: {
    onPointerDown: () => void;
    onPointerUp: () => void;
    onPointerLeave: () => void;
  };
}

/**
 * Apple-style press/release animation.
 * On press: scale(0.97), 80ms linear
 * On release: scale(1), 350ms bounce easing
 * Respects prefers-reduced-motion.
 */
export function useAppleMotion(): AppleMotionResult {
  const ref = useRef<HTMLElement | null>(null);
  const [pressed, setPressed] = useState(false);

  const onPointerDown = useCallback(() => {
    if (prefersReducedMotion()) return;
    setPressed(true);
  }, []);

  const onPointerUp = useCallback(() => {
    setPressed(false);
  }, []);

  const onPointerLeave = useCallback(() => {
    setPressed(false);
  }, []);

  const style: CSSProperties = {
    transform: pressed ? `scale(${APPLE_PRESS.scale})` : 'scale(1)',
    transition: pressed
      ? `transform ${APPLE_PRESS.duration}ms ${APPLE_PRESS.easing}`
      : `transform ${APPLE_RELEASE.duration}ms ${APPLE_RELEASE.easing}`,
  };

  return {
    ref,
    style,
    handlers: { onPointerDown, onPointerUp, onPointerLeave },
  };
}

/**
 * Hook for staggered card entrance animations.
 * Returns a className and style for each item based on its index.
 */
export function useStaggerAnimation(index: number): { className: string; style: CSSProperties } {
  if (prefersReducedMotion()) {
    return { className: '', style: {} };
  }

  return {
    className: 'animate-[cardEnter_250ms_var(--ease-spring)_both]',
    style: { animationDelay: `${index * DELAY.stagger}ms` },
  };
}

/**
 * Returns class/style for page-level entrance animation.
 */
export function usePageEnter(): { className: string } {
  if (prefersReducedMotion()) {
    return { className: '' };
  }
  return { className: 'animate-[pageEnter_200ms_ease-out_both]' };
}

/**
 * Returns class for message appearance animation.
 */
export function useMessageAppear(): { className: string } {
  if (prefersReducedMotion()) {
    return { className: '' };
  }
  return { className: 'animate-[messageAppear_250ms_var(--ease-spring)_both]' };
}

/**
 * Asymmetric hover timing — fast snap-in, slow ease-out.
 * Matches the Apple/mockup pattern: 120ms on hover, 200ms on leave.
 */
export function useAsymmetricHover(enterMs = 120, leaveMs = 200): {
  hovered: boolean;
  transitionDuration: string;
  handlers: {
    onPointerEnter: () => void;
    onPointerLeave: () => void;
  };
} {
  const [hovered, setHovered] = useState(false);

  const onPointerEnter = useCallback(() => {
    if (prefersReducedMotion()) return;
    setHovered(true);
  }, []);

  const onPointerLeave = useCallback(() => {
    setHovered(false);
  }, []);

  return {
    hovered,
    transitionDuration: hovered ? `${enterMs}ms` : `${leaveMs}ms`,
    handlers: { onPointerEnter, onPointerLeave },
  };
}
